import React, { useCallback } from 'react';
import { FormattedMessage, useIntl } from 'react-intl';
import ScopedAssistantChat from '@components/ScopedAssistantChat';
import { trackFunnelEvent } from '@utils/datadog';
import { useProcessAssistantConversation } from './useProcessAssistantConversation';

interface ProcessAssistantChatProps {
  processId: string;
  onProcessEdited: () => void;
  onBusyChange: (busy: boolean) => void;
  onBeforeSend: () => Promise<boolean>;
  dataTestId: string;
}

/**
 * The chat itself, split from the panel shell so the conversation is only
 * resolved — and ``useChat``'s streaming machinery only mounted — while the
 * panel is actually open.
 *
 * ``onBusyChange`` freezes the canvas for a turn and releases it on unmount;
 * the panel keeps this mounted while collapsed. ``onProcessEdited`` pulls what
 * the assistant wrote when its turn ends; a turn that saved nothing costs one
 * conditional GET — see {@link useProcessGraphSync}. ``onBeforeSend`` lands
 * anything still in the autosave debounce first, since the assistant reads
 * the graph from the server.
 */
const ProcessAssistantChat: React.FC<ProcessAssistantChatProps> = ({
  processId,
  onProcessEdited,
  onBusyChange,
  onBeforeSend,
  dataTestId,
}) => {
  const intl = useIntl();
  const conversation = useProcessAssistantConversation(processId);
  const trackSent = useCallback(
    () => trackFunnelEvent('process_assistant_message_sent', { processId }),
    [processId],
  );

  return (
    <ScopedAssistantChat
      conversation={conversation}
      onTurnFinished={onProcessEdited}
      onBusyChange={onBusyChange}
      onBeforeSend={onBeforeSend}
      onSent={trackSent}
      unavailableMessage={
        <FormattedMessage
          id="processes.assistant.unavailable"
          defaultMessage="The process assistant isn't available for this process."
        />
      }
      placeholder={intl.formatMessage({
        id: 'processes.assistant.placeholder',
        defaultMessage: 'Describe your process…',
      })}
      dataTestId={dataTestId}
    />
  );
};

export default ProcessAssistantChat;
